import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { opportunitiesApi } from '../../api/opportunities'
import { RefusedError } from '../../api/client'
import { DocumentDrop } from '../../components/Documents/DocumentDrop'
import { DocumentFacts } from '../../components/Documents/DocumentFacts'
import { DocumentList } from '../../components/Documents/DocumentList'
import { formatDate } from '../../lib/format'
import { statusWords } from '../../lib/outbox'
import type { OutboundStatus } from '../../types/outbox'
import type { OpportunityEdit, OpportunityFocus } from '../../types/workbench'
import { PERSONA_WORDS } from './stages'

// The engagement as an opportunity on its own page (REQ-122): the people in it, "set up by you" with an inline edit
// when it was, and every correspondence written on it with where each stands in the Outbox. The plays applied are
// the lead page's own "Plays run" panel; they are not repeated here. Nothing here sends.

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))
const dateOnly = (iso?: string | null) => (iso ? iso.slice(0, 10) : '')

/** Under the pursuit heading: the people chips, the "set up by you" word and the edit that goes with it. */
export function OpportunityPeople({ spoke, opportunity }: { spoke: string; opportunity: OpportunityFocus }) {
  const [editing, setEditing] = useState(false)
  return (
    <div className="opportunity-people-row">
      <div className="opportunity-people-line">
        {opportunity.people.length === 0
          ? <span className="muted small">Nobody named in it yet.</span>
          : <ul className="chips" aria-label="People in it">{opportunity.people.map((p) => <li key={p.personId} className="chip on" title={[p.title, p.persona ? PERSONA_WORDS[p.persona] ?? p.persona : null].filter(Boolean).join(' · ') || undefined}>{p.name}</li>)}</ul>}
        {opportunity.byHand && <span className="tag by-hand">set up by you</span>}
        {opportunity.byHand && !editing && <button type="button" className="button small" onClick={() => setEditing(true)}>Edit</button>}
      </div>
      {opportunity.byHand && editing && <OpportunityEditForm spoke={spoke} opportunity={opportunity} onDone={() => setEditing(false)} />}
    </div>
  )
}

/** The inline edit: the subject only while no thread has joined, the summary, the next step and its date. The stage
 *  is not here; it goes through "Not right?" above. Only what changed is sent. */
function OpportunityEditForm({ spoke, opportunity, onDone }: { spoke: string; opportunity: OpportunityFocus; onDone: () => void }) {
  const qc = useQueryClient()
  const [subject, setSubject] = useState(opportunity.subject)
  const [summary, setSummary] = useState(opportunity.summary)
  const [nextStep, setNextStep] = useState(opportunity.nextStep ?? '')
  const [nextStepBy, setNextStepBy] = useState(dateOnly(opportunity.nextStepBy))
  const canRename = !opportunity.hasThread
  const edit = useMutation({
    mutationFn: (body: OpportunityEdit) => opportunitiesApi.edit(spoke, opportunity.engagementId, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['lead', spoke, opportunity.engagementId] }); onDone() },
  })
  const submit = () => {
    const body: OpportunityEdit = {}
    if (canRename && subject.trim() && subject.trim() !== opportunity.subject) body.subject = subject.trim()
    if (summary.trim() !== opportunity.summary) body.summary = summary.trim()
    if (nextStep.trim() !== (opportunity.nextStep ?? '')) body.nextStep = nextStep.trim()
    if (nextStepBy && nextStepBy !== dateOnly(opportunity.nextStepBy)) body.nextStepBy = nextStepBy
    if (Object.keys(body).length === 0) { onDone(); return }
    edit.mutate(body)
  }
  return (
    <form className="opportunity-edit" onSubmit={(e) => { e.preventDefault(); submit() }} aria-label="Edit the opportunity">
      <label>Subject
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!canRename || edit.isPending} required
          title={canRename ? undefined : 'A thread has joined this opportunity; the subject is the thread’s now.'} />
        {!canRename && <span className="muted small">A thread has joined it; the subject is the thread's now.</span>}
      </label>
      <label>Summary
        <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} disabled={edit.isPending} />
      </label>
      <div className="opportunity-edit-row">
        <label>Next step
          <input type="text" value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="what happens next" disabled={edit.isPending} />
        </label>
        <label>By
          <input type="date" value={nextStepBy} onChange={(e) => setNextStepBy(e.target.value)} disabled={edit.isPending} />
        </label>
      </div>
      {edit.isError && <p className="error small" role="alert">{why(edit.error)}</p>}
      <div className="actions">
        <button type="submit" className="button small primary" disabled={edit.isPending}>{edit.isPending ? 'Saving…' : 'Save'}</button>
        <button type="button" className="button small" onClick={onDone} disabled={edit.isPending}>Cancel</button>
      </div>
    </form>
  )
}

/** The documents on this opportunity (REQ-119) and what they said (REQ-120): the drop zone, the rows, the facts.
 *  A reading that lands refreshes the lead so the facts show; nothing here writes to the opportunity itself. */
export function OpportunityDocuments({ spoke, opportunity }: { spoke: string; opportunity: OpportunityFocus }) {
  const qc = useQueryClient()
  const leadKey = ['lead', spoke, opportunity.engagementId]
  const facts = opportunity.documentFacts ?? []
  return (
    <div className="correspondence opportunity-documents">
      <h3>Documents on this opportunity</h3>
      <DocumentDrop spoke={spoke} origin="opportunity" targets={{ engagementId: opportunity.engagementId }} onUploaded={() => void qc.invalidateQueries({ queryKey: leadKey })} />
      <DocumentList spoke={spoke} targets={{ engagement: opportunity.engagementId }} invalidate={[leadKey]} />
      {facts.length > 0 && (
        <>
          <h3>What the documents say</h3>
          <DocumentFacts facts={facts} />
        </>
      )}
    </div>
  )
}

/** Every message written on this opportunity, with where it stands; each row opens the Outbox. */
export function OpportunityCorrespondence({ opportunity }: { opportunity: OpportunityFocus }) {
  const rows = [...opportunity.correspondences].sort((a, b) => (b.sentAt ?? b.updatedAt).localeCompare(a.sentAt ?? a.updatedAt))
  const tacticName = (id?: string | null) => (id ? opportunity.tactics.find((t) => t.tacticId === id)?.tacticName ?? id : null)
  return (
    <div className="correspondence">
      <h3>Correspondence on this opportunity</h3>
      {rows.length === 0 ? <p className="muted small">Nothing written on it yet. Work it in the workbench and the messages land here.</p> : (
        <ul className="plain correspondence-list">
          {rows.map((c) => (
            <li key={c.outboundId} className={`correspondence-row status-${c.status}`}>
              <Link to="/outbox" className="correspondence-link">
                <span className="correspondence-to"><strong>{c.toName}</strong></span>
                <span className="correspondence-subject">{c.subject}</span>
              </Link>
              <span className="correspondence-meta">
                <span className={`tag status ${c.status}`}>{statusWords(c.status as OutboundStatus)}</span>
                <span className="muted small">{c.sentAt ? `sent ${formatDate(c.sentAt)}` : `updated ${formatDate(c.updatedAt)}`}{c.tacticId ? ` · play: ${tacticName(c.tacticId)}` : ''}{c.outcome ? ` · ${c.outcome}` : ''}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
